import React, { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { Pagination, Menu, Dropdown } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { debounce } from "lodash";

import CommonAgGridTable from "../../../../../../../utility/CommonAgGridTable";

import CustomAgGridHeader from "../../../../../../../utility/CustomAgGridHeader";
import { useDragColumnSync } from "../../../../../Report/pages/SharedComponents/useDragColumnSync";
import * as settingsActions from "../../../../../Estore/_redux/Estore/basicSettingsAction";
import * as columnFormatters from "./column-formatters";
import { AgingTableFilters } from "./AgingTableFilters";

import { SIZE_PER_PAGE_LIST } from "../../../../../Common/constants";
import { customTotal, formatCurrency, getSettingKey, getTimeByTimeZone, navigateToInvoiceHistoryByOverduePeriod } from "../../../../../Common/utils";
import { useDashboardBalanceVisibility } from "../../balanceVisibility";

const getOverdueDays = (data) => {
  const dueDate = new Date(data?.due_date);
  const currentDate = new Date();
  dueDate.setHours(23, 59, 59, 0);

  const daysUntilDue = Math.floor((dueDate - currentDate) / (1000 * 60 * 60 * 24));
  return -daysUntilDue;
};

const AgingColumnHeader = (props) => {
  const { displayName, onSelectCreatedAt, onSelectHighToLow, isHighToLowActive } = props;

  const menu = (
    <Menu>
      <Menu.Item key="createdAt" onClick={onSelectCreatedAt}>
        Least Overdue
      </Menu.Item>
      <Menu.Item key="highToLow" onClick={onSelectHighToLow}>
        Most Overdue
      </Menu.Item>
    </Menu>
  );

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        height: "100%",
        padding: "0 4px",
      }}
    >
      <span>{displayName}</span>
      <Dropdown overlayClassName="custom-aggrid-header-dropdown" overlay={menu} trigger={["click"]}>
        <button className="custom-header-menu-button" onClick={(e) => e.stopPropagation()}>
          ⋮
        </button>
      </Dropdown>
    </div>
  );
};

export function AgingTable({ agingInvoices, queryParams, setQueryParams, listLoading }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { currentBasicState } = useSelector((state) => ({ currentBasicState: state.basicSettings }), shallowEqual);
  const { dragcolumnlisting } = currentBasicState;
  const timezone = getSettingKey(currentBasicState.entity, "Basic", "timezone");
  const currencySymbol = getSettingKey(currentBasicState.entity, "Basic", "currency_symbol");
  const { formatCurrencyValue } = useDashboardBalanceVisibility();
  const getAgingInvoiceRows = (data) => {
    const rows = Array.isArray(data)
      ? data
      : data?.entities || data?.results || data?.data || data?.invoices || [];

    return rows.flatMap((item) => (Array.isArray(item?.recs) ? item.recs : item));
  };
  const agingInvoiceRows = getAgingInvoiceRows(agingInvoices);
  const totalCount = agingInvoices?.totalCount || agingInvoiceRows.length || 0;
  const currentPage = queryParams?.page || 1;
  const pageSize = queryParams?.page_size || agingInvoices?.pageSize || 20;
  const pageSizeOptions = SIZE_PER_PAGE_LIST.map((list) => list.value);

  const [isDragged, setIsDragged] = useState(false);
  const [columnSelection, setColumnSelection] = useState(false);
  const [isUserAction, setIsUserAction] = useState(false);
  const [pinnedCount, setPinnedCount] = useState(() => {
    return dragcolumnlisting?.pinnedCount ?? 0;
  });
  const [isAgingHighToLowActive, setIsAgingHighToLowActive] = useState(false);

  const defaultColumns = useMemo(
    () => [
      {
        dataIndex: "order_date",
        title: "Order Date",
        checked: true,
        width: 150,
        minWidth: 120,
        disableSortingMenu: true,
      },
      {
        dataIndex: "invoice_no",
        title: "Invoice No.",
        checked: true,
        width: 150,
        minWidth: 120,
        disableSortingMenu: true,
      },
      {
        dataIndex: "business_name",
        title: "Business Name",
        checked: true,
        width: 150,
        minWidth: 120,
        disableSortingMenu: true,
      },
      {
        dataIndex: "due_date",
        title: "Due Date",
        checked: true,
        width: 150,
        minWidth: 120,
        disableSortingMenu: true,
      },
      {
        dataIndex: "amount",
        title: "Amount",
        checked: true,
        width: 150,
        minWidth: 120,
        disableSortingMenu: true,
        cellRenderer: ({ data }) => (
          <div>
            <p>{formatCurrencyValue(data?.amount, `${currencySymbol} `, (amount) => formatCurrency(amount || "").toLocaleString())}</p>
          </div>
        ),
      },
      {
        dataIndex: "aging",
        title: "Aging",
        checked: true,
        width: 150,
        minWidth: 120,
        disableSortingMenu: true,
        cellRenderer: ({ data }) => columnFormatters.InvoiceAgingColumnFormatter(data),
      },
    ],
    [currencySymbol, formatCurrencyValue]
  );

  const [selectedColumns, setSelectedColumns] = useState(defaultColumns);

  useEffect(() => {
    const savedColumns = dragcolumnlisting?.selectedColumns;
    if (!Array.isArray(savedColumns) || savedColumns.length === 0) {
      setSelectedColumns(defaultColumns);
      return;
    }

    setSelectedColumns(
      savedColumns.map((col) => {
        const original = defaultColumns.find((c) => c.dataIndex === col.dataIndex);
        return {
          ...original,
          ...col,
          cellRenderer: original?.cellRenderer,
          minWidth: col.minWidth ?? original?.minWidth ?? 120,
        };
      })
    );
  }, [dragcolumnlisting, defaultColumns]);

  useEffect(() => {
    if (dragcolumnlisting?.pinnedCount !== undefined) {
      setPinnedCount(dragcolumnlisting.pinnedCount);
    }
  }, [dragcolumnlisting]);

  const debouncedSaveColumnWidths = debounce((dispatch, updatedColumns, pinCount) => {
    dispatch(
      settingsActions.setDragColumnListing(
        {
          type: "admin_panel",
          sub_type: "Dashboard.Aging.Listing.Column",
          force: 1,
          validate_columns: false,
        },
        {
          selectedColumns: updatedColumns,
          pinnedCount: pinCount,
        }
      )
    );
  }, 300);

  useDragColumnSync({
    type: "admin_panel",
    subType: "Dashboard.Aging.Listing.Column",
    selectedColumns,
    isDragged,
    setIsDragged,
    columns: defaultColumns,
    columnSelection,
    setColumnSelection,
    pinnedCount,
    validate_columns: false,
  });

  const gridApiRef = useRef(null);
  const columnApiRef = useRef(null);

  const handleSelectCreatedAt = () => {
    setIsAgingHighToLowActive(false);
    columnApiRef.current?.applyColumnState({ state: [{ colId: "aging", sort: null }], defaultState: { sort: null } });
    setQueryParams((prev) => ({ ...prev, sort_by: "created_at:asc", page: 1 }));
  };

  const handleSelectHighToLow = () => {
      setIsAgingHighToLowActive(false);
      setQueryParams((prev) => ({ ...prev, sort_by: "created_at:desc", page: 1 }));
      columnApiRef.current?.applyColumnState({ state: [{ colId: "aging", sort: null }], defaultState: { sort: null } });
      return;
  };

  const agGridColumnDefs = [
    ...selectedColumns
      .filter((col) => col.checked)
      .map((col, idx, arr) => {
        const def = {
          headerName: col.title,
          field: col.dataIndex,
          sortable: col.dataIndex === "aging",
          resizable: true,
          headerComponent: col.dataIndex === "aging" ? AgingColumnHeader : CustomAgGridHeader,
          headerComponentParams:
            col.dataIndex === "aging"
              ? {
                  onSelectCreatedAt: handleSelectCreatedAt,
                  onSelectHighToLow: handleSelectHighToLow,
                  isHighToLowActive: isAgingHighToLowActive,
                }
              : {
                  selectedColumns,
                  setSelectedColumns,
                  pinnedCount,
                  debouncedSaveColumnWidths,
                  dispatch,
                  disableSortingMenu: true,
                },
          width: Number(col.width) || 200,
          minWidth: Number(col.minWidth) || 120,
        };
        if (col.dataIndex === "aging") {
          def.comparator = (valueA, valueB, nodeA, nodeB) => getOverdueDays(nodeA?.data) - getOverdueDays(nodeB?.data);
        }
        if (idx === arr.length - 1) {
          def.flex = 1;
        }
        if (col.cellRenderer) {
          def.cellRenderer = col.cellRenderer;
        }
        if (col.dataIndex === "order_date") {
          def.cellRenderer = ({ data }) => {
            const order_date = data.order_date || "";
            const convertedTimezone = getTimeByTimeZone(timezone, order_date, "MM/DD/YYYY");

            return <span style={{ fontSize: "14px" }}>{convertedTimezone}</span>;
          };
        }
        if (col.dataIndex === "due_date") {
          def.cellRenderer = ({ data }) => {
            const due_date = data.due_date || "";
            const convertedTimezone = getTimeByTimeZone(timezone, due_date, "MM/DD/YYYY");
            return <span style={{ fontSize: "14px" }}>{convertedTimezone}</span>;
          };
        }
        return def;
      }),
  ];

  const handlePageChange = (page, page_size) => {
    setQueryParams((prev) => ({ ...prev, page, page_size }));
  };

  const onShowSizeChange = (page, page_size) => {
    setQueryParams((prev) => ({ ...prev, page: 1, page_size }));
  };

  return (
    <div>
      <h2>Aging</h2>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}>
          <AgingTableFilters setQueryParams={setQueryParams} />
        </div>
        <div
          style={{ color: "red", cursor: "pointer", textDecoration: "underline", marginBottom: "1rem", marginLeft: "16px" }}
          onClick={() => navigateToInvoiceHistoryByOverduePeriod(history)}
        >
          View All
        </div>
      </div>

      <CommonAgGridTable
        rowData={agingInvoiceRows}
        columnDefs={agGridColumnDefs}
        pinnedCount={pinnedCount}
        setPinnedCount={setPinnedCount}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        setIsDragged={setIsDragged}
        isUserAction={isUserAction}
        setIsUserAction={setIsUserAction}
        listLoading={listLoading}
        type="admin_panel"
        subType="Dashboard.Aging.Listing.Column"
        validateColumns={false}
        rowSelection="multiple"
        rowSelectionEnabled={true}
        style={{ height: "500px" }}
        onGridReady={(api, columnApi) => {
          gridApiRef.current = api;
          columnApiRef.current = columnApi;
        }}
      />
      <Pagination
        style={{ marginTop: "5px", paddingLeft: "20px" }}
        total={totalCount}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        current={currentPage}
        showSizeChanger={true}
        showTotal={(total, range) => customTotal(range[0], range[1], total, "aging invoices")}
        onChange={handlePageChange}
        onShowSizeChange={onShowSizeChange}
      />
    </div>
  );
}
